import React, { useState, useEffect } from 'react';
import { callAI } from '../services/aiService';
import { storageService } from '../services/storageService';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n';
import { handlePostToX } from '../utils/handlePostToX';
import { useToast } from './Toast';

type ReplyTone = 'Empathy' | 'Question' | 'Humor' | 'Expert' | 'Casual';

interface ReplyCandidate {
  text: string;
  score: number;
  tone: ReplyTone;
}

const toneOptions: { value: ReplyTone; icon: string; labelKey: string; descKey: string }[] = [
  { value: 'Empathy', icon: 'EMP', labelKey: 'replyGuy.tones.empathy', descKey: 'replyGuy.tones.empathyDesc' },
  { value: 'Question', icon: '?', labelKey: 'replyGuy.tones.question', descKey: 'replyGuy.tones.questionDesc' },
  { value: 'Humor', icon: 'HA', labelKey: 'replyGuy.tones.humor', descKey: 'replyGuy.tones.humorDesc' },
  { value: 'Expert', icon: 'PRO', labelKey: 'replyGuy.tones.expert', descKey: 'replyGuy.tones.expertDesc' },
  { value: 'Casual', icon: 'HI', labelKey: 'replyGuy.tones.casual', descKey: 'replyGuy.tones.casualDesc' },
];

const STORAGE_KEY_TWEET = 'replyguy_original_tweet';
const STORAGE_KEY_TONE = 'replyguy_tone';
const STORAGE_KEY_REPLIES = 'replyguy_replies';

export const ReplyGuy: React.FC = () => {
  const { t } = useT();
  const { toast } = useToast();
  const [originalTweet, setOriginalTweet] = useState(() => localStorage.getItem(STORAGE_KEY_TWEET) || '');
  const [selectedTone, setSelectedTone] = useState<ReplyTone>(() => (localStorage.getItem(STORAGE_KEY_TONE) as ReplyTone) || 'Empathy');
  const [status, setStatus] = useState<'idle' | 'generating' | 'complete' | 'error'>('idle');
  const [replies, setReplies] = useState<ReplyCandidate[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_REPLIES);
    return saved ? JSON.parse(saved) : [];
  });
  const [error, setError] = useState<string | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  useEffect(() => { localStorage.setItem(STORAGE_KEY_TWEET, originalTweet); }, [originalTweet]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY_TONE, selectedTone); }, [selectedTone]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY_REPLIES, JSON.stringify(replies)); }, [replies]);

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 1500);
  };

  const handleGenerate = async () => {
    if (!originalTweet.trim()) return;
    setStatus('generating');
    setError(null);

    try {
      const styleModel = storageService.getStyleModel();
      const styleContext = styleModel?.systemPrompt ? `\n\n【Your writing style】\n${styleModel.systemPrompt}` : '';

      const prompt = `Generate 5 replies to the following tweet in a "${selectedTone}" tone.

【Original tweet】
${originalTweet}
${styleContext}

【Rules】
1. Each reply must be under 280 characters
2. Must feel like a natural conversation
3. Maintain the "${selectedTone}" tone throughout
4. Provide variety across replies

【Output format (JSON)】
{
  "replies": ["reply 1", "reply 2", "reply 3", "reply 4", "reply 5"]
}`;

      const response = await callAI(
        [
          { role: 'system', content: 'You are an expert at crafting Twitter replies that spark engagement. Generate natural, conversation-starting replies.' },
          { role: 'user', content: prompt }
        ],
        { temperature: 0.8, maxTokens: 1500, jsonMode: true }
      );

      const parsed = JSON.parse(response);
      const replyTexts: string[] = parsed.replies || [];

      const scored: ReplyCandidate[] = replyTexts.map(text => ({
        text,
        score: calculateReplyScore(text, selectedTone),
        tone: selectedTone,
      })).sort((a, b) => b.score - a.score);

      setReplies(scored);
      setStatus('complete');

      if (scored.length > 0) {
        storageService.addHistoryItem({ type: 'reply', content: scored[0].text, score: scored[0].score, metadata: { tone: selectedTone, inputText: originalTweet.slice(0, 200) } });
      }
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to generate replies');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px] mx-auto">
      <div className="mb-6 lg:mb-[30px]">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1.5 tracking-tight text-text-primary">{t('replyGuy.title')}</h1>
        <p className="text-text-muted text-sm sm:text-[15px] leading-relaxed">{t('replyGuy.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Left: Input */}
        <div>
          <h2 className="text-lg lg:text-xl mb-4 text-text-primary">{t('replyGuy.originalTweet')}</h2>
          <textarea
            value={originalTweet}
            onChange={(e) => setOriginalTweet(e.target.value)}
            placeholder={t('replyGuy.placeholder')}
            className="w-full min-h-[180px] p-4 lg:p-5 bg-surface-2 border border-border rounded-xl text-text-primary text-base resize-y font-[inherit] leading-[1.6] placeholder:text-text-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          />

          <h3 className="text-base mt-5 mb-3 text-text-primary">{t('replyGuy.replyTone')}</h3>
          <div className="flex flex-wrap gap-2.5 mb-5">
            {toneOptions.map(tone => (
              <button
                key={tone.value}
                onClick={() => setSelectedTone(tone.value)}
                className={cn(
                  'px-4 py-2.5 rounded-xl text-sm transition-all',
                  selectedTone === tone.value
                    ? 'bg-success/15 border-2 border-success text-success font-bold'
                    : 'bg-surface-2 border border-border text-text-primary hover:border-text-muted'
                )}
              >
                {tone.icon} {t(tone.labelKey)}
              </button>
            ))}
          </div>
          <p className="text-[13px] text-text-muted mb-5">
            {t(toneOptions.find(tone => tone.value === selectedTone)?.descKey || '')}
          </p>

          <button
            onClick={handleGenerate}
            disabled={!originalTweet.trim() || status === 'generating'}
            className={cn(
              'w-full py-4 border-none rounded-3xl text-base font-bold transition-all',
              !originalTweet.trim() || status === 'generating'
                ? 'bg-surface-3 text-text-faint cursor-not-allowed'
                : 'bg-gradient-to-r from-success to-emerald-400 text-white cursor-pointer hover:opacity-90'
            )}
          >
            {status === 'generating' ? t('common.generating') : t('replyGuy.generateReplies')}
          </button>
        </div>

        {/* Right: Results */}
        <div>
          <h2 className="text-lg lg:text-xl mb-4 text-text-primary">{t('replyGuy.topReplies')}</h2>

          {error && (
            <div className="p-4 bg-surface-2 border border-danger rounded-xl mb-4">
              <p className="text-danger m-0">{error}</p>
            </div>
          )}

          {replies.length > 0 ? (
            <div className="flex flex-col gap-3">
              {replies.map((reply, index) => (
                <div
                  key={index}
                  onClick={() => handleCopy(reply.text, index)}
                  className="p-4 bg-surface-2 border border-border rounded-xl cursor-pointer transition-all hover:border-success hover:bg-success/5"
                >
                  <div className="flex justify-between mb-2">
                    <span className="text-xs text-text-muted">#{index + 1}</span>
                    <span className={cn(
                      'text-[13px] font-bold',
                      reply.score >= 8 ? 'text-success' : reply.score >= 6 ? 'text-brand' : 'text-text-muted'
                    )}>
                      {t('common.score')}: {reply.score}/10
                    </span>
                  </div>
                  <p className="text-[15px] leading-[1.6] m-0 text-text-primary">{reply.text}</p>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-text-muted">
                      {copiedIndex === index ? `${t('common.copied')}` : t('common.clickToCopy')}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePostToX(reply.text, toast, t); }}
                        className="px-3 py-1 bg-surface-1 border border-border text-text-muted rounded-lg text-xs font-medium hover:bg-surface-3 hover:text-text-primary transition-all cursor-pointer"
                      >{t('common.postToX')}</button>
                      <button
                        onClick={(e) => { e.stopPropagation(); storageService.addSavedIdea(reply.text); }}
                        className="bg-transparent border-none text-text-muted cursor-pointer text-sm p-0 hover:text-brand"
                        title={t('common.savedToIdeas')}
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-[300px] bg-surface-2 border-2 border-dashed border-border rounded-xl text-text-muted text-base text-center whitespace-pre-line">
              {t('replyGuy.emptyState')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function calculateReplyScore(text: string, tone: ReplyTone): number {
  let score = 5;
  const len = text.length;
  if (len >= 30 && len <= 150) score += 2;
  else if (len >= 10 && len <= 200) score += 1;
  if (tone === 'Question' && text.includes('?')) score += 1;
  if (tone === 'Empathy' && (text.toLowerCase().includes('agree') || text.toLowerCase().includes('same') || text.toLowerCase().includes('feel'))) score += 1;
  if (tone === 'Humor' && text.length < 100) score += 1;
  score += Math.random() * 1.5;
  return Math.min(10, Math.round(score * 10) / 10);
}
